import aboutImg from "../assets/about-us-img.png"; // update path
import Navbar from "./Navbar";
import { motion } from "framer-motion";
import {
    CheckCircle, Search,
    ClipboardList,
    PencilRuler,
    Code2,
    Clock3,
} from "lucide-react";
import EnrollSection from "./EnrollSection";

const features = [
    "Industry Expert Trainers",
    "Hands-on Practical Learning",
    "100% Placement Assistance",
];

const process = [
    {
        number: "01",
        title: "Discover",
        description: "We understand your requirements",
        icon: Search,
    },
    {
        number: "02",
        title: "Plan",
        description: "We plan the best solution",
        icon: ClipboardList,
    },
    {
        number: "03",
        title: "Design",
        description: "We design creative and modern UI/UX",
        icon: PencilRuler,
    },
    {
        number: "04",
        title: "Develop",
        description: "We build your solution with clean code",
        icon: Code2,
    },
    {
        number: "05",
        title: "Deliver",
        description: "We test and deliver the final product",
        icon: Clock3,
    },
];


const AboutUs = () => {
    return (

        <section className="w-full bg-white">
            {/* ===== HERO / ABOUT SECTION ===== */}
            <Navbar />

            <section className="py-24 bg-gradient-to-r from-white via-blue-50 to-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-6">

                    <div className="grid lg:grid-cols-2 gap-16 items-center">

                        {/* LEFT CONTENT */}
                        <motion.div
                            initial={{ opacity: 0, x: -60 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: .8 }}
                        >

                            {/* Small Heading */}
                            <p className="text-[#075DA8] font-bold text-xl mb-4">
                                About Us
                            </p>

                            {/* Main Heading */}
                            <h2 className="text-5xl lg:text-6xl font-bold leading-tight text-gray-900">
                                We Transform
                                <br />
                                <span className="text-[#075DA8]">
                                    Learning Into
                                </span>
                                <br />
                                Career Success
                            </h2>

                            {/* Description */}
                            <p className="mt-8 text-lg leading-9 text-gray-600">
                                MSM Technologies is committed to providing
                                industry-focused IT training that equips students
                                with practical knowledge, real-world experience,
                                and the confidence to succeed in today's
                                competitive technology landscape.
                            </p>

                            {/* Feature List */}
                            <div className="mt-10 space-y-5">

                                {features.map((item, index) => (
                                    <motion.div
                                        key={index}
                                        className="flex items-center gap-4"
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{
                                            duration: .5,
                                            delay: index * .15,
                                        }}
                                    >
                                        <div className="w-9 h-9 rounded-full bg-[#075DA8] flex items-center justify-center">
                                            <CheckCircle
                                                size={18}
                                                className="text-white"
                                            />
                                        </div>

                                        <span className="text-lg text-gray-700">
                                            {item}
                                        </span>
                                    </motion.div>
                                ))}

                            </div>
                        </motion.div>

                        {/* RIGHT IMAGE */}
                        <motion.div
                            className="relative"
                            initial={{ opacity: 0, x: 60 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: .8 }}
                        >

                            <img
                                src={aboutImg}
                                alt="About"
                                className="rounded-[30px] w-full object-cover shadow-2xl"
                            />

                            {/* Floating Experience Card */}
                            <motion.div
                                whileHover={{
                                    y: -8,
                                    scale: 1.05,
                                }}
                                className="absolute -bottom-8 right-6 bg-white rounded-2xl shadow-xl px-8 py-6 border"
                            >
                                <h3 className="text-4xl font-bold text-[#075DA8]">
                                    3+
                                </h3>

                                <p className="text-gray-600 font-medium leading-7">
                                    Years
                                    <br />
                                    Experience
                                </p>
                            </motion.div>

                        </motion.div>

                    </div>

                </div>
            </section>


            <section className="py-24 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-6">

                    {/* Heading */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="text-center"
                    >
                        <h2 className="text-5xl font-bold text-[#075DA8]">
                            Our Process
                        </h2>

                        <p className="mt-5 text-xl text-gray-600">
                            We follow a proven process to deliver successful projects.
                        </p>
                    </motion.div>

                    {/* Timeline */}
                    <div className="relative mt-20">

                        {/* Horizontal Line */}
                        <div className="hidden lg:block absolute top-10 left-20 right-20 h-[2px] bg-gray-300"></div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">

                            {process.map((item, index) => {
                                const Icon = item.icon;

                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 50 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{
                                            duration: 0.6,
                                            delay: index * 0.15,
                                        }}
                                        className="relative text-center"
                                    >
                                        {/* Step Number */}
                                        <div className="absolute left-0 top-3 w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-gray-600 text-xl">
                                            {item.number}
                                        </div>

                                        {/* Icon Circle */}
                                        <motion.div
                                            whileHover={{
                                                y: -8,
                                                scale: 1.08,
                                            }}
                                            className="mx-auto w-20 h-20 rounded-full border border-gray-400 bg-blue-100 flex items-center justify-center relative z-10"
                                        >
                                            <Icon size={34} />
                                        </motion.div>

                                        {/* Title */}
                                        <h3 className="mt-6 text-2xl font-semibold text-gray-900">
                                            {item.title}
                                        </h3>

                                        {/* Description */}
                                        <p className="mt-3 text-gray-500 leading-7">
                                            {item.description}
                                        </p>
                                    </motion.div>
                                );
                            })}

                        </div>
                    </div>
                </div>
            </section>

            {/* ===== WHY MSM TECHNOLOGIES ===== */}
            <div className="w-full bg-[#075DA8] text-white py-16">
                <div className="max-w-4xl mx-auto px-6">
                    <h2 className="text-3xl font-semibold text-center mb-12">
                        Why MSM Technologies?
                    </h2>

                    <div className="grid justify-content-center grid-cols-1 md:grid-cols-2 gap-5">

                        {/* ITEM */}
                        <div className="flex gap-4">
                            <span className="text-2xl">✔</span>
                            <div>
                                <h4 className="font-semibold">
                                    Experienced Development Team
                                </h4>
                                {/* <p className="text-sm opacity-90 mt-1">
                                    MSM Technologies has transformed the careers of thousands of students,
                                    turning their ambitions into achievements.
                                </p> */}
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <span className="text-2xl">✔</span>
                            <div>
                                <h4 className="font-semibold">
                                    Modern & Responsive Designs
                                </h4>
                                {/* <p className="text-sm opacity-90 mt-1">
                                    From industry projects to continuous placement guidance, we prepare
                                    learners for the corporate world.
                                </p> */}
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <span className="text-2xl">✔</span>
                            <div>
                                <h4 className="font-semibold">
                                    Dedicated Customer Support
                                </h4>
                                {/* <p className="text-sm opacity-90 mt-1">
                                    Our strong placement network offers students excellent opportunities
                                    across leading companies.
                                </p> */}
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <span className="text-2xl">✔</span>
                            <div>
                                <h4 className="font-semibold">
                                    SEO-Friendly Solutions
                                </h4>
                                {/* <p className="text-sm opacity-90 mt-1">
                                    Small batch sizes ensure individual attention and better concept clarity.
                                </p> */}
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <span className="text-2xl">✔</span>
                            <div>
                                <h4 className="font-semibold">
                                    Scalable & Secure Applications
                                </h4>
                                {/* <p className="text-sm opacity-90 mt-1">
                                    Our trainers bring real-time corporate experience, providing practical and up-to-date industry knowledge.
                                </p> */}
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <span className="text-2xl">✔</span>
                            <div>
                                <h4 className="font-semibold">
                                    100% Client Satisfaction Focus
                                </h4>
                                {/* <p className="text-sm opacity-90 mt-1">
                                    Our trainers bring real-time corporate experience, providing practical and up-to-date industry knowledge.
                                </p> */}
                            </div>
                        </div>

                    </div>
                </div>
            </div>

           <EnrollSection/>

        </section>
    );
};

export default AboutUs;
